import { z } from 'zod';

import { getTranslation } from '../../../../../utils/get-translation.util';

export const MAX_FILE_SIZE = 1024 * 1024 * 10;

export const ACCEPTED_FILE_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'image/png',
  'image/jpeg',
];

export const fileSchemaMessage = getTranslation('FILE_SIZE_TOO_LARGE');

const returnedToPoolField = z.coerce
  .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
  .min(0, { message: getTranslation('REQUIRED') });

const getExercisable = (grant: { exercisedCount: number; vestedSharesCount: number }) =>
  grant.vestedSharesCount - grant.exercisedCount;

const withReturnToPoolValidation = <
  T extends {
    grant: { exercisedCount: number; vestedSharesCount: number };
    returnedToPool: number;
  },
>(
  schema: z.ZodType<T>,
  getExercised: (data: T) => number,
  exercisedPath: string,
) =>
  schema
    .refine((data) => getExercised(data) + data.returnedToPool > 0, {
      message: getTranslation('EXERCISED_OR_RETURN_TO_POOL_REQUIRED'),
      path: [exercisedPath],
    })
    .refine((data) => getExercised(data) <= getExercisable(data.grant) - data.returnedToPool, {
      message: getTranslation('OPTIONS_MUST_BE_LESS_THAN_BALANCE'),
      path: [exercisedPath],
    })
    .refine((data) => data.returnedToPool <= getExercisable(data.grant) - getExercised(data), {
      message: getTranslation('RETURN_TO_POOL_EXCEEDS_REMAINING'),
      path: ['returnedToPool'],
    });

const commonStock = z.object({
  stockItems: z
    .array(
      withReturnToPoolValidation(
        z.object({
          stakeholder: z.object({
            fullName: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          grant: z.object({
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            exercisedCount: z.coerce
              .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
              .min(0),
            vestedSharesCount: z.coerce.number({
              message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
            }),
            grantItem: z.object({
              plan: z.object({
                strikePrice: z.coerce.number({
                  message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
                }),
                conversionRatio: z.coerce.number({
                  message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
                }),
                pool: z.object({
                  shareClass: z.object({
                    name: z
                      .string()
                      .trim()
                      .min(1, { message: getTranslation('REQUIRED') }),
                  }),
                }),
              }),
            }),
          }),
          date: z.coerce.date(),
          exercised: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(0, { message: getTranslation('REQUIRED') }),
          returnedToPool: returnedToPoolField,
        }),
        ({ exercised }) => exercised,
        'exercised',
      ),
    )
    .min(1, { message: getTranslation('REQUIRED') })
    .max(10, { message: getTranslation('ITEMS_MAXIMUM', { count: 10 }) }),
});

const sar = z.object({
  sarItems: z
    .array(
      z
        .object({
          stakeholder: z.object({
            fullName: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          grant: z.object({
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            exercisedCount: z.coerce
              .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
              .min(0),
            vestedSharesCount: z.coerce.number({
              message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
            }),
            grantItem: z.object({
              plan: z.object({
                sarBasePrice: z.coerce.number({
                  message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
                }),
                pool: z.object({
                  shareClass: z.object({
                    name: z
                      .string()
                      .trim()
                      .min(1, { message: getTranslation('REQUIRED') }),
                  }),
                }),
              }),
            }),
          }),
          sharePrice: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(1, { message: getTranslation('REQUIRED') }),
          date: z.coerce.date(),
          sarCount: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(0, { message: getTranslation('REQUIRED') }),
          sarPayout: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(0, { message: getTranslation('REQUIRED') }),
          issuedShares: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(0, { message: getTranslation('REQUIRED') }),
          returnedToPool: returnedToPoolField,
        })
        .refine(({ sarCount, returnedToPool }) => sarCount + returnedToPool > 0, {
          message: getTranslation('EXERCISED_OR_RETURN_TO_POOL_REQUIRED'),
          path: ['sarCount'],
        })
        .refine(
          ({ sarCount, grant, returnedToPool }) => {
            return sarCount <= grant.vestedSharesCount - grant.exercisedCount - returnedToPool;
          },
          {
            message: getTranslation('SARS_MUST_BE_LESS_THAN_BALANCE'),
            path: ['sarCount'],
          },
        )
        .refine(
          ({ sarCount, grant, returnedToPool }) => {
            return returnedToPool <= grant.vestedSharesCount - grant.exercisedCount - sarCount;
          },
          {
            message: getTranslation('RETURN_TO_POOL_EXCEEDS_REMAINING'),
            path: ['returnedToPool'],
          },
        )
        .refine(
          ({ sarCount, grant, sarPayout, issuedShares, sharePrice }) => {
            return (
              sarCount ===
              (issuedShares * sharePrice) / (sharePrice - grant.grantItem.plan.sarBasePrice) +
                sarPayout / (sharePrice - grant.grantItem.plan.sarBasePrice)
            );
          },
          {
            message: getTranslation('ALL_SARS_MUST_BE_DISTRIBUTED'),
            path: [],
          },
        ),
    )
    .min(1, { message: getTranslation('REQUIRED') })
    .max(10, { message: getTranslation('ITEMS_MAXIMUM', { count: 10 }) }),
});

const phantom = z.object({
  phantomItems: z
    .array(
      withReturnToPoolValidation(
        z.object({
          stakeholder: z.object({
            fullName: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          grant: z.object({
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            exercisedCount: z.coerce
              .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
              .min(0),
            vestedSharesCount: z.coerce.number({
              message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
            }),
          }),
          date: z.coerce.date(),
          sharePrice: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(1, { message: getTranslation('REQUIRED') }),
          phantomShares: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(0, { message: getTranslation('REQUIRED') }),
          returnedToPool: returnedToPoolField,
        }),
        ({ phantomShares }) => phantomShares,
        'phantomShares',
      ),
    )
    .min(1, { message: getTranslation('REQUIRED') })
    .max(10, { message: getTranslation('ITEMS_MAXIMUM', { count: 10 }) }),
});

const hurdle = z.object({
  planId: z.string(),
  hurdleItems: z
    .array(
      z
        .object({
          selected: z.boolean(),
          stakeholder: z.object({
            fullName: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          grantId: z
            .string()
            .trim()
            .min(1, { message: getTranslation('REQUIRED') }),
          hurdleValue: z.coerce.number({
            message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
          }),
          shareClassName: z
            .string()
            .trim()
            .min(1, { message: getTranslation('REQUIRED') }),
          date: z.coerce.date(),
          vestedSharesCount: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(0),
          exercisedCount: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(0),
          issuedShares: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(1, { message: getTranslation('REQUIRED') }),
        })
        .refine(
          ({ issuedShares, vestedSharesCount, exercisedCount }) =>
            issuedShares <= vestedSharesCount - exercisedCount,
          {
            message: getTranslation('SHARES_MUST_BE_LESS_THAN_BALANCE'),
            path: ['issuedShares'],
          },
        ),
    )
    .min(1, { message: getTranslation('REQUIRED') })
    .refine((items) => items.some(({ selected }) => selected), {
      message: getTranslation('REQUIRED'),
      path: ['hurdleItems'],
    }),
});

const bspce = z.object({
  bspceItems: z
    .array(
      withReturnToPoolValidation(
        z.object({
          stakeholder: z.object({
            fullName: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          grant: z.object({
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            exercisedCount: z.coerce
              .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
              .min(0),
            vestedSharesCount: z.coerce.number({
              message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
            }),
            grantItem: z.object({
              plan: z.object({
                strikePrice: z.coerce.number({
                  message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
                }),
                conversionRatio: z.coerce.number({
                  message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
                }),
                pool: z.object({
                  shareClass: z.object({
                    name: z
                      .string()
                      .trim()
                      .min(1, { message: getTranslation('REQUIRED') }),
                  }),
                }),
              }),
            }),
          }),
          date: z.coerce.date(),
          exercised: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(0, { message: getTranslation('REQUIRED') }),
          returnedToPool: returnedToPoolField,
        }),
        ({ exercised }) => exercised,
        'exercised',
      ),
    )
    .min(1, { message: getTranslation('REQUIRED') })
    .max(10, { message: getTranslation('ITEMS_MAXIMUM', { count: 10 }) }),
});

const warrantExercise = z.object({
  warrantExerciseItems: z
    .array(
      withReturnToPoolValidation(
        z.object({
          stakeholder: z.object({
            fullName: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          grant: z.object({
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            exercisedCount: z.coerce
              .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
              .min(0),
            vestedSharesCount: z.coerce.number({
              message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
            }),
            grantItem: z.object({
              plan: z.object({
                warrantPrice: z.coerce.number({
                  message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
                }),
                conversionRatio: z.coerce.number({
                  message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
                }),
                expiryDate: z.coerce.date(),
                pool: z.object({
                  shareClass: z.object({
                    name: z
                      .string()
                      .trim()
                      .min(1, { message: getTranslation('REQUIRED') }),
                  }),
                }),
              }),
            }),
          }),
          date: z.coerce.date(),
          exercised: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(0, { message: getTranslation('REQUIRED') }),
          returnedToPool: returnedToPoolField,
        }),
        ({ exercised }) => exercised,
        'exercised',
      ),
    )
    .min(1, { message: getTranslation('REQUIRED') })
    .max(10, { message: getTranslation('ITEMS_MAXIMUM', { count: 10 }) }),
});

const warrantExpire = z.object({
  warrantExpireItems: z
    .array(
      z
        .object({
          stakeholder: z.object({
            fullName: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          grant: z.object({
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            exercisedCount: z.coerce
              .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
              .min(0),
            vestedSharesCount: z.coerce.number({
              message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
            }),
            grantItem: z.object({
              plan: z
                .object({
                  expiryDate: z.coerce.date(),
                })
                .refine(
                  ({ expiryDate }) => {
                    if (!expiryDate) {
                      return false;
                    }
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    expiryDate.setHours(0, 0, 0, 0);
                    return expiryDate <= today;
                  },
                  {
                    message: 'Expiry date must not be in the future',
                    path: ['expiryDate'],
                  },
                ),
            }),
          }),
          expired: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(1, { message: getTranslation('REQUIRED') }),
        })
        .refine(
          ({ expired, grant }) => {
            return expired <= grant.vestedSharesCount;
          },
          {
            message: 'Warrants must be less than or equal to balance',
            path: ['expired'],
          },
        ),
    )
    .min(1, { message: getTranslation('REQUIRED') })
    .max(10, { message: getTranslation('ITEMS_MAXIMUM', { count: 10 }) }),
});

export const stepOne = z.object({
  commonStock: commonStock.optional(),
  sar: sar.optional(),
  phantom: phantom.optional(),
  hurdle: hurdle.optional(),
  bspce: bspce.optional(),
  warrantExercise: warrantExercise.optional(),
  warrantExpire: warrantExpire.optional(),
});

export const stepTwo = z
  .object({
    additionalNotes: z
      .string()
      .max(3000, getTranslation('MAXIMUM_CHARACTERS', { count: 3000 }))
      .optional(),
    files: z
      .object({
        docLink: z.string(),
        loadProgress: z.number().max(100),
        abort: z.function(),
        id: z.string(),
        doc: z
          .custom<File>()
          .or(
            z.object({
              size: z.number(),
              type: z.string(),
              name: z.string(),
            }),
          )
          .refine(
            ({ type }) => ACCEPTED_FILE_MIME_TYPES.includes(type),
            getTranslation('FILE_FORMAT_NOT_SUPPORTED'),
          )
          .refine(({ size }) => size <= MAX_FILE_SIZE, fileSchemaMessage),
      })
      .array()

      .optional(),
  })
  .optional();

export const formSchema = z.object({
  stepOne,
  stepTwo,
});

export type FormSchema = z.infer<typeof formSchema>;
